import { useEffect, useMemo, useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { chatQueryKeys, ChatUnreadCount, getChatUnreadCounts } from 'services/api/chat';

// A station tick ({chatConversationIds, actorId, occurredAt} — flags/ids, never content)
// or the id-free channel-set flag ({chatChannelSet: true}).
type ChatActivityEvent = {
  chatConversationIds?: string[];
  actorId?: string | null;
  occurredAt?: string;
  chatChannelSet?: boolean;
};

/**
 * Unread counts: an authoritative snapshot (mount, window focus, cable reconnect) plus a
 * live ledger fed by ONE multiplexed ChatActivityChannel subscription. The client sends
 * only its context (the chattable pivot); the server derives and maintains the station set,
 * re-deriving it on channel-set changes and on a timer. Events that beat the initial
 * snapshot buffer, then replay filtered by occurredAt > servedAt so nothing the snapshot
 * counted replays on top of it. A channel-set flag means "refetch the channel list" — pure
 * UI news; the stations already resynced server-side. Returns counts keyed by conversation
 * id — useChatConversations merges them at render.
 */
export default function useChatUnreadCounts(workflowId: string | undefined) {
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  // Server-side moment the cached snapshot was computed; only the boot replay reads it.
  const servedAtRef = useRef('');

  const query = useQuery({
    queryKey: chatQueryKeys.unreadCounts(workflowId ?? 'missing'),
    queryFn: async ({ signal }) => {
      const { servedAt, data } = await getChatUnreadCounts(workflowId!, signal);
      servedAtRef.current = servedAt;
      return data;
    },
    enabled: !!workflowId,
    // Opt back in (global default is false): the focus refetch is how read-state from
    // other tabs/devices reconciles.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const counts = query.data;
  const snapshotLoaded = counts !== undefined;
  const snapshotLoadedRef = useRef(snapshotLoaded);
  snapshotLoadedRef.current = snapshotLoaded;

  // Events that beat the initial snapshot; everConnected distinguishes reconnects.
  const bufferRef = useRef<ChatActivityEvent[]>([]);
  const everConnectedRef = useRef(false);

  // Reset per workflow — the page survives navigation between referrals.
  useEffect(() => {
    bufferRef.current = [];
    everConnectedRef.current = false;
  }, [workflowId]);

  const applyEvents = (events: ChatActivityEvent[]) => {
    if (!workflowId) return;
    const increments = new Map<string, number>();
    for (const event of events) {
      if (event.actorId === profile.id) continue; // own sends never badge yourself
      for (const conversationId of event.chatConversationIds ?? []) {
        increments.set(conversationId, (increments.get(conversationId) ?? 0) + 1);
      }
    }
    if (increments.size === 0) return;

    queryClient.setQueryData<ChatUnreadCount[]>(chatQueryKeys.unreadCounts(workflowId), (rows) => {
      if (!rows) return rows;
      const seen = new Set(rows.map((row) => row.chatConversationId));
      const updated = rows.map((row) =>
        increments.has(row.chatConversationId)
          ? { ...row, unreadCount: row.unreadCount + increments.get(row.chatConversationId)! }
          : row
      );
      // A channel born after the snapshot has no row yet.
      for (const [conversationId, count] of increments) {
        if (!seen.has(conversationId)) updated.push({ chatConversationId: conversationId, unreadCount: count });
      }
      return updated;
    });
  };
  const applyEventsRef = useRef(applyEvents);
  applyEventsRef.current = applyEvents;

  useWebsocketSubscription<ChatActivityEvent>({
    channel: 'ChatActivityChannel',
    // snake_case — cable subscribe frames skip OliveBranch. Context only: the server
    // derives the station set from the policy scope; nothing here influences authorization.
    params: { chattable_type: 'Workflow', chattable_id: workflowId },
    enabled: !!workflowId,
    onConnected: () => {
      // On RE-connect (offline gap or a server-side access disconnect) re-snapshot and
      // refetch the channel list — both may have drifted.
      if (everConnectedRef.current && workflowId) {
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.unreadCounts(workflowId) });
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.index(workflowId) });
      }
      everConnectedRef.current = true;
    },
    onReceived: (event) => {
      // Channel set changed: stations already resynced server-side — just refetch the
      // policy-scoped channel list. Never buffered — it isn't a count.
      if (event.chatChannelSet) {
        if (workflowId) queryClient.invalidateQueries({ queryKey: chatQueryKeys.index(workflowId) });
        return;
      }
      if (!snapshotLoadedRef.current) {
        bufferRef.current.push(event);
        return;
      }
      applyEventsRef.current([event]);
    },
  });

  // Replay buffered events the snapshot did NOT already count (occurredAt > servedAt).
  useEffect(() => {
    if (!snapshotLoaded || bufferRef.current.length === 0) return;
    const buffered = bufferRef.current;
    bufferRef.current = [];
    applyEventsRef.current(buffered.filter((event) => (event.occurredAt ?? '') > servedAtRef.current));
  }, [snapshotLoaded]);

  // On snapshot failure drop the buffer — the next successful snapshot includes them.
  const isError = query.isError;
  useEffect(() => {
    if (isError) bufferRef.current = [];
  }, [isError]);

  const countsById = useMemo(
    () => new Map((counts ?? []).map((row) => [row.chatConversationId, row.unreadCount])),
    [counts]
  );

  return { countsById };
}
